import React, { useState, useEffect } from 'react';
import { getAuthToken } from '../api/api';
import { useAuth } from '../context/AuthContext';
import { useVistaEmpleado } from '../context/VistaEmpleadoContext';
import { Clock, Calendar, AlertCircle, Coffee, Users, Timer, Pause } from 'lucide-react';
import FichajeRapido, { estadoFichaje } from '../components/empleados/FichajeRapido';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Fichaje = () => {
  const { user } = useAuth();
  // "Simular vista empleado": se preselecciona el empleado simulado y no se ficha
  const { simulando, vistaEmpleado, usuarioEfectivo } = useVistaEmpleado();
  const idSimulado = simulando ? vistaEmpleado.id : null;
  const [empleados, setEmpleados] = useState([]);
  const [empleadoActual, setEmpleadoActual] = useState(null);
  const [fichajeHoy, setFichajeHoy] = useState(null);
  const [jornadasHoy, setJornadasHoy] = useState([]);
  const [horaActual, setHoraActual] = useState(new Date());
  const [loading, setLoading] = useState(true);
  const [resumenSemana, setResumenSemana] = useState({ horas_trabajadas: 0, dias_trabajados: 0 });
  const [fichajesEquipo, setFichajesEquipo] = useState([]);
  const [empleadoSeleccionado, setEmpleadoSeleccionado] = useState('');

  // Actualizar hora cada segundo
  useEffect(() => {
    const interval = setInterval(() => {
      setHoraActual(new Date());
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  // Cargar datos al montar
  useEffect(() => {
    cargarDatos();
  }, [user, idSimulado]);

  const cargarDatos = async () => {
    setLoading(true);
    try {
      const token = getAuthToken();
      
      // Cargar todos los empleados activos
      const empleadosRes = await fetch(`${API_URL}/api/empleados?activo=true`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (empleadosRes.ok) {
        const emps = await empleadosRes.json();
        setEmpleados(emps);
        
        // Buscar empleado por email del usuario o por empleado_id (en la vista
        // de simulación, el simulado)
        let empleado = null;
        if (idSimulado) {
          empleado = emps.find(e => e.id === idSimulado) || null;
        } else if (user?.email) {
          empleado = emps.find(e => e.email === user.email);
        }
        if (!empleado && user?.empleado_id) {
          empleado = emps.find(e => e.id === user.empleado_id);
        }
        
        if (!empleado) {
          setEmpleadoActual(null);
          setEmpleadoSeleccionado('');
          setFichajeHoy(null);
          setJornadasHoy([]);
        }
        if (empleado) {
          setEmpleadoActual(empleado);
          setEmpleadoSeleccionado(empleado.id);
          await cargarFichajeHoy(empleado.id);
          await cargarResumenSemana(empleado.id);
        }
      }
      
      // Cargar fichajes del equipo hoy
      await cargarFichajesEquipo();
      
    } catch (error) {
      console.error('Error cargando datos:', error);
    } finally {
      setLoading(false);
    }
  };

  const cargarFichajeHoy = async (empleadoId) => {
    try {
      const token = getAuthToken();
      // Misma fecha que usa el servidor al fichar (UTC). Se pide también ayer
      // para poder cerrar un turno que pasó de medianoche; nada más antiguo.
      const ahora = new Date();
      const hoy = ahora.toISOString().split('T')[0];
      const ayer = new Date(ahora.getTime() - 24 * 60 * 60 * 1000).toISOString().split('T')[0];

      const res = await fetch(`${API_URL}/api/fichaje/empleado/${empleadoId}?fecha_inicio=${ayer}&fecha_fin=${hoy}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (res.ok) {
        const data = await res.json();
        const porEntrada = (a, b) => String(a.hora_entrada || '').localeCompare(String(b.hora_entrada || ''));
        const registrosHoy = (data.registros || []).filter(r => r.fecha === hoy).sort(porEntrada);
        const abiertaAyer = (data.registros || [])
          .filter(r => r.fecha === ayer && r.hora_entrada && !r.hora_salida)
          .sort(porEntrada)
          .pop();
        if (registrosHoy.length > 0 || abiertaAyer) {
          // Jornada abierta (sin salida): la de hoy o, si no hay, la de ayer
          const jornadaAbierta = registrosHoy.find(r => r.hora_entrada && !r.hora_salida) || abiertaAyer;
          if (jornadaAbierta) {
            setFichajeHoy(jornadaAbierta);
          } else {
            // Si no hay jornada abierta, mostrar la última cerrada
            setFichajeHoy(registrosHoy[registrosHoy.length - 1]);
          }
          // Jornadas del día (y la de ayer si sigue abierta)
          setJornadasHoy(abiertaAyer ? [abiertaAyer, ...registrosHoy] : registrosHoy);
        } else {
          setFichajeHoy(null);
          setJornadasHoy([]);
        }
      }
    } catch (error) {
      console.error('Error cargando fichaje de hoy:', error);
    }
  };

  const cargarResumenSemana = async (empleadoId) => {
    try {
      const token = getAuthToken();
      
      // Calcular inicio de semana (lunes)
      const hoy = new Date();
      const diaSemana = hoy.getDay();
      const diasHastaLunes = diaSemana === 0 ? 6 : diaSemana - 1;
      const inicioSemana = new Date(hoy);
      inicioSemana.setDate(hoy.getDate() - diasHastaLunes);
      
      const fechaInicio = inicioSemana.toISOString().split('T')[0];
      const fechaFin = hoy.toISOString().split('T')[0];
      
      const res = await fetch(`${API_URL}/api/fichaje/empleado/${empleadoId}?fecha_inicio=${fechaInicio}&fecha_fin=${fechaFin}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        let totalMinutos = 0;
        let diasTrabajados = 0;
        
        if (data.registros) {
          data.registros.forEach(r => {
            if (r.minutos_trabajados) {
              totalMinutos += r.minutos_trabajados;
              diasTrabajados++;
            }
          });
        }
        
        setResumenSemana({
          horas_trabajadas: totalMinutos / 60,
          dias_trabajados: diasTrabajados
        });
      }
    } catch (error) {
      console.error('Error cargando resumen:', error);
    }
  };

  const cargarFichajesEquipo = async () => {
    try {
      const token = getAuthToken();
      const res = await fetch(`${API_URL}/api/fichaje/hoy`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        setFichajesEquipo(data.registros || []);
      }
    } catch (error) {
      console.error('Error cargando fichajes del equipo:', error);
    }
  };

  const handleSeleccionEmpleado = async (empleadoId) => {
    const emp = empleados.find(e => e.id === empleadoId);
    if (emp) {
      setEmpleadoActual(emp);
      setEmpleadoSeleccionado(empleadoId);
      await cargarFichajeHoy(empleadoId);
      await cargarResumenSemana(empleadoId);
    }
  };

  const formatHora = (datetime) => {
    if (!datetime) return '--:--';
    const d = new Date(datetime);
    return d.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });
  };

  const calcularTiempoTrabajando = () => {
    if (!fichajeHoy?.hora_entrada || fichajeHoy?.hora_salida) return null;
    
    const entrada = new Date(fichajeHoy.hora_entrada);
    const ahora = new Date();
    const diffMs = ahora.getTime() - entrada.getTime();
    
    // Si la diferencia es negativa (problema de zona horaria), retornar 0
    if (diffMs < 0) return '0h 0m';
    
    const diffHoras = Math.floor(diffMs / (1000 * 60 * 60));
    const diffMinutos = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
    
    return `${diffHoras}h ${diffMinutos}m`;
  };

  const horasContrato = empleadoActual?.horas_contrato_semana || 40;
  const porcentajeHoras = Math.min((resumenSemana.horas_trabajadas / horasContrato) * 100, 100);
  const horasRestantes = Math.max(horasContrato - resumenSemana.horas_trabajadas, 0);

  // Colores para empleados
  const getColorClasses = (color) => {
    const colores = {
      amarillo: 'bg-yellow-400 text-yellow-900',
      azul: 'bg-blue-500 text-white',
      verde: 'bg-green-500 text-white',
      naranja: 'bg-orange-500 text-white',
      morado: 'bg-purple-500 text-white',
      rosa: 'bg-pink-400 text-white',
      rojo: 'bg-red-500 text-white',
      cyan: 'bg-cyan-500 text-white',
    };
    return colores[color] || 'bg-gray-400 text-white';
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-xl text-gray-600">Cargando fichajes...</div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-0 py-2 sm:px-4 sm:py-8">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-4">
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-800">Fichaje</h1>
        <div className="text-left md:text-right">
          <div className="text-4xl font-bold text-gray-900">
            {horaActual.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
          </div>
          <div className="text-sm text-gray-500">
            {horaActual.toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
          </div>
        </div>
      </div>

      {/* Selector de empleado - Solo visible para administradores (rol: direccion).
          En la vista de simulación se ve lo que vería el empleado simulado. */}
      {usuarioEfectivo?.rol === 'direccion' && (
        <div className="bg-white rounded-lg shadow-md p-4 mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            <Users className="w-4 h-4 inline mr-1" />
            Seleccionar Empleado (Vista Administrador)
          </label>
          <select
            value={empleadoSeleccionado}
            onChange={(e) => handleSeleccionEmpleado(e.target.value)}
            className="w-full md:w-64 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="">-- Seleccionar empleado --</option>
            {[...empleados].sort((a, b) => a.nombre.localeCompare(b.nombre)).map(emp => (
              <option key={emp.id} value={emp.id}>
                {emp.nombre} {emp.nombre_completo && emp.nombre_completo !== emp.nombre ? `(${emp.nombre_completo})` : ''}
              </option>
            ))}
          </select>
        </div>
      )}

      {/* Panel de fichaje */}
      {empleadoActual ? (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
          {/* Info del empleado y botones */}
          <div className="lg:col-span-2 bg-white rounded-lg shadow-md p-4 sm:p-6">
            <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
              <div className="flex items-center space-x-4 min-w-0">
                <div className={`w-14 h-14 rounded-full ${empleadoActual.color ? getColorClasses(empleadoActual.color) : 'bg-blue-500 text-white'} flex items-center justify-center text-2xl font-bold`}>
                  {empleadoActual.nombre?.charAt(0)?.toUpperCase() || '?'}
                </div>
                <div>
                  <h2 className="text-xl font-bold text-gray-900">{empleadoActual.nombre}</h2>
                  <p className="text-sm text-gray-500 capitalize">{empleadoActual.rol}</p>
                </div>
              </div>
              
              {/* Estado actual (en móvil ya lo enseñan los botones de fichaje) */}
              <div className="hidden sm:block text-right">
                {fichajeHoy?.en_pausa ? (
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-amber-100 text-amber-800">
                    <Pause className="w-4 h-4 mr-1" />
                    En pausa
                  </span>
                ) : fichajeHoy?.hora_entrada && !fichajeHoy?.hora_salida ? (
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
                    <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse mr-2"></div>
                    Trabajando
                  </span>
                ) : fichajeHoy?.hora_salida ? (
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
                    <Coffee className="w-4 h-4 mr-1" />
                    Jornada cerrada
                  </span>
                ) : (
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-gray-100 text-gray-800">
                    <Clock className="w-4 h-4 mr-1" />
                    Sin fichar
                  </span>
                )}
              </div>
            </div>

            {/* Botones de fichaje (componente compartido con Mi Inicio) */}
            <div className="mb-4" data-tour="fichaje-boton">
              <FichajeRapido
                empleadoId={empleadoActual.id}
                estado={estadoFichaje(fichajeHoy)}
                horaEntrada={fichajeHoy?.hora_entrada}
                horaSalida={fichajeHoy?.hora_salida}
                soloLectura={simulando}
                onFichado={async () => {
                  await cargarFichajeHoy(empleadoActual.id);
                  await cargarResumenSemana(empleadoActual.id);
                  await cargarFichajesEquipo();
                }}
              />
              {/* Info de pausas del día */}
              {fichajeHoy?.hora_entrada && !fichajeHoy?.hora_salida && fichajeHoy?.pausas && fichajeHoy.pausas.length > 0 && (
                <div className="mt-2 text-sm text-gray-600">
                  <span className="font-medium">Pausas hoy:</span> {fichajeHoy.pausas.length} ({fichajeHoy.total_pausa_minutos || 0} min total)
                </div>
              )}
            </div>

            {/* Jornadas del día */}
            {jornadasHoy.length > 0 && (
              <div className="mb-4">
                <h4 className="text-sm font-medium text-gray-700 mb-2">Jornadas de hoy ({jornadasHoy.length})</h4>
                <div className="space-y-2">
                  {jornadasHoy.map((jornada, idx) => (
                    <div key={jornada.id} className={`flex flex-wrap gap-y-1 items-center justify-between p-3 rounded-lg ${
                      jornada.en_pausa ? 'bg-amber-50 border border-amber-200' :
                      !jornada.hora_salida ? 'bg-green-50 border border-green-200' : 'bg-gray-50'
                    }`}>
                      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                        <span className="text-sm font-medium text-gray-600">
                          Jornada {idx + 1}
                        </span>
                        <span className="text-sm text-gray-700">
                          {formatHora(jornada.hora_entrada)} → {jornada.hora_salida ? formatHora(jornada.hora_salida) : '...'}
                        </span>
                        {jornada.en_pausa && (
                          <span className="text-xs px-2 py-0.5 bg-amber-200 text-amber-800 rounded">En pausa</span>
                        )}
                        {jornada.total_pausa_minutos > 0 && !jornada.en_pausa && (
                          <span className="text-xs text-gray-500">({jornada.total_pausa_minutos} min pausa)</span>
                        )}
                      </div>
                      <span className={`text-sm font-bold ${!jornada.hora_salida ? 'text-green-600' : 'text-gray-700'}`}>
                        {jornada.hora_salida 
                          ? `${Math.floor((jornada.minutos_trabajados || 0) / 60)}h ${(jornada.minutos_trabajados || 0) % 60}m`
                          : 'En curso'
                        }
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Detalles del fichaje actual */}
            {fichajeHoy && !fichajeHoy.hora_salida && (
              <div className="grid grid-cols-3 gap-4 p-4 bg-gray-50 rounded-lg">
                <div className="text-center">
                  <Clock className="w-5 h-5 mx-auto text-green-600 mb-1" />
                  <p className="text-xs text-gray-500">Entrada</p>
                  <p className="text-lg font-bold text-gray-900">{formatHora(fichajeHoy.hora_entrada)}</p>
                </div>
                
                <div className="text-center">
                  <Clock className="w-5 h-5 mx-auto text-red-600 mb-1" />
                  <p className="text-xs text-gray-500">Salida</p>
                  <p className="text-lg font-bold text-gray-900">--:--</p>
                </div>
                
                <div className="text-center">
                  <Timer className="w-5 h-5 mx-auto text-blue-600 mb-1" />
                  <p className="text-xs text-gray-500">Tiempo</p>
                  <p className="text-lg font-bold text-gray-900">
                    {calcularTiempoTrabajando() || '0h 0m'}
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Resumen semanal */}
          <div className="bg-white rounded-lg shadow-md p-4 sm:p-6" data-tour="fichaje-resumen">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <Calendar className="w-5 h-5 mr-2 text-blue-600" />
              Resumen Semanal
            </h3>
            
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-blue-50 rounded-lg p-3 sm:p-4 border border-blue-200">
                <div className="text-xl sm:text-2xl font-bold text-blue-800">{resumenSemana.horas_trabajadas.toFixed(1)}h</div>
                <div className="text-sm text-blue-600">Horas trabajadas</div>
              </div>
              
              <div className="bg-gray-50 rounded-lg p-3 sm:p-4 border border-gray-200">
                <div className="text-xl sm:text-2xl font-bold text-gray-800">{horasContrato}h</div>
                <div className="text-sm text-gray-600">Horas contrato</div>
              </div>
              
              <div className={`rounded-lg p-3 sm:p-4 border ${horasRestantes > 0 ? 'bg-amber-50 border-amber-200' : 'bg-green-50 border-green-200'}`}>
                <div className={`text-xl sm:text-2xl font-bold ${horasRestantes > 0 ? 'text-amber-800' : 'text-green-800'}`}>
                  {horasRestantes.toFixed(1)}h
                </div>
                <div className={`text-sm ${horasRestantes > 0 ? 'text-amber-600' : 'text-green-600'}`}>Horas restantes</div>
              </div>
              
              <div className="bg-purple-50 rounded-lg p-3 sm:p-4 border border-purple-200">
                <div className="text-xl sm:text-2xl font-bold text-purple-800">{resumenSemana.dias_trabajados}</div>
                <div className="text-sm text-purple-600">Días trabajados</div>
              </div>
            </div>
            
            {/* Barra de progreso */}
            <div className="mt-4">
              <div className="flex justify-between text-sm mb-1">
                <span className="text-gray-600">Progreso</span>
                <span className="font-medium text-gray-900">{porcentajeHoras.toFixed(0)}%</span>
              </div>
              <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                <div
                  style={{ width: `${porcentajeHoras}%` }}
                  className={`h-full transition-all duration-500 ${
                    porcentajeHoras >= 100 ? 'bg-green-500' : porcentajeHoras >= 80 ? 'bg-blue-500' : 'bg-amber-500'
                  }`}
                />
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-6 mb-6">
          <div className="flex items-center">
            <AlertCircle className="w-8 h-8 text-amber-500 mr-3" />
            <div>
              {usuarioEfectivo?.rol === 'direccion' ? (
                <>
                  <h2 className="text-lg font-semibold text-amber-800">Selecciona un empleado</h2>
                  <p className="text-amber-600">Usa el selector de arriba para elegir un empleado y registrar su fichaje.</p>
                </>
              ) : (
                <>
                  <h2 className="text-lg font-semibold text-amber-800">No se encontró tu perfil de empleado</h2>
                  <p className="text-amber-600">Contacta con administración para vincular tu cuenta a tu ficha de empleado.</p>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Estado del equipo hoy */}
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        <div className="px-4 sm:px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center">
            <Users className="w-5 h-5 mr-2 text-blue-600" />
            Estado del Equipo Hoy
          </h3>
        </div>
        
        {fichajesEquipo.length > 0 ? (
          <div className="divide-y divide-gray-200">
            {fichajesEquipo.map((fichaje, idx) => (
              <div key={idx} className="px-4 sm:px-6 py-4 flex flex-wrap gap-2 items-center justify-between hover:bg-gray-50">
                <div className="flex items-center space-x-3">
                  <div className={`w-10 h-10 rounded-full ${fichaje.empleado_color ? getColorClasses(fichaje.empleado_color) : 'bg-gray-400 text-white'} flex items-center justify-center font-bold`}>
                    {fichaje.empleado_nombre?.charAt(0)?.toUpperCase() || '?'}
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">{fichaje.empleado_nombre}</p>
                    <p className="text-xs text-gray-500">
                      Entrada: {formatHora(fichaje.hora_entrada)}
                      {fichaje.hora_salida && ` • Salida: ${formatHora(fichaje.hora_salida)}`}
                    </p>
                  </div>
                </div>
                
                <div>
                  {fichaje.hora_salida ? (
                    <span className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm">
                      {Math.floor((fichaje.minutos_trabajados || 0) / 60)}h {(fichaje.minutos_trabajados || 0) % 60}m
                    </span>
                  ) : (
                    <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm flex items-center">
                      <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse mr-2"></div>
                      Trabajando
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="px-6 py-8 text-center text-gray-500">
            Nadie ha fichado hoy todavía
          </div>
        )}
      </div>
    </div>
  );
};

export default Fichaje;
